<script lang="ts">
  import { push } from "svelte-spa-router";
  import {
    Info,
    CircleAlert,
    Loader2,
    Eye,
    EyeOff,
    Activity,
    ShieldCheck,
    Building2,
    Droplets,
    Flame,
    Zap,
  } from "lucide-svelte";
  import { fetchUserProfile } from "./lib/stores/user";
  import { onMount } from "svelte";
  import {
    consumeSessionExpiredNotice,
    consumeSessionReturnTo,
  } from "./lib/utils/sessionExpiry";
  import sayaLogo from "./assets/saya_logo.png";

  let username = $state("");
  let password = $state("");
  let showPassword = $state(false);
  let loading = $state(false);
  let error = $state("");
  let invalidField = $state<"username" | "password" | "">("");
  let sessionExpired = $state(false);
  let usernameInput: HTMLInputElement;
  let passwordInput: HTMLInputElement;

  onMount(() => {
    sessionExpired = consumeSessionExpiredNotice();
  });

  function clearError() {
    error = "";
    invalidField = "";
  }

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (loading) return;
    error = "";
    invalidField = "";

    if (username.length < 3) {
      invalidField = "username";
      error = "Enter a username with at least 3 characters.";
      usernameInput?.focus();
      return;
    }
    if (password.length < 6) {
      invalidField = "password";
      error = "Enter a password with at least 6 characters.";
      passwordInput?.focus();
      return;
    }

    loading = true;
    try {
      const res = await fetch("/login", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      if (res.ok) {
        try {
          localStorage.setItem("auth_hint", "1");
        } catch {}
        const authenticated = await fetchUserProfile();
        if (authenticated) {
          push(consumeSessionReturnTo());
        } else {
          error = "Your session could not be restored. Please sign in again.";
        }
      } else if (res.status === 401 || res.status === 400) {
        error =
          "The username or password is incorrect. Check both and try again.";
      } else if (res.status === 429) {
        error = "Too many sign-in attempts. Please wait and try again.";
      } else {
        error = "Sign-in is temporarily unavailable. Please try again.";
      }
    } catch {
      error = "Unable to connect. Check your connection and try again.";
    } finally {
      loading = false;
    }
  }
</script>

<main class="login-page">
  <div class="login-container">
    <!-- Left Hero Brand Panel (Desktop) -->
    <div class="login-hero" aria-hidden="true">
      <div class="hero-bg-mesh"></div>
      <div class="hero-content">
        <div class="hero-header">
          <div class="hero-brand">
            <img src={sayaLogo} alt="Saya Logo" class="hero-logo-img" />
          </div>
        </div>

        <div class="hero-lead">
          <h2 class="hero-title">AI-Native Utility OS</h2>
          <p class="hero-desc">
            Unified telemetry, predictive anomaly intelligence, and automated
            containment across water, gas, and electricity.
          </p>
        </div>

        <div class="hero-points">
          <div class="point-item">
            <div class="point-icon-box">
              <div class="flex items-center gap-1">
                <Droplets class="w-3.5 h-3.5 text-cyan-400" />
                <Flame class="w-3.5 h-3.5 text-amber-400" />
                <Zap class="w-3.5 h-3.5 text-yellow-400" />
              </div>
            </div>
            <div>
              <div class="point-title">Multi-Utility Submetering</div>
              <div class="point-detail">
                Continuous precision monitoring across water, gas, and
                electricity
              </div>
            </div>
          </div>

          <div class="point-item">
            <div class="point-icon-box">
              <Activity class="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <div class="point-title">Sub-Second AI Telemetry</div>
              <div class="point-detail">
                Real-time surge and leak alerts with automated safety shutoff
              </div>
            </div>
          </div>

          <div class="point-item">
            <div class="point-icon-box">
              <Building2 class="w-4 h-4 text-blue-400" />
            </div>
            <div>
              <div class="point-title">Single-Family & Multi-Tenant</div>
              <div class="point-detail">
                From individual smart residences to large commercial portfolios
              </div>
            </div>
          </div>
        </div>

        <div class="hero-footer-note">
          <span class="text-xs text-white/50"
            >Trusted across single-family homes & multi-tenant enterprise
            communities</span
          >
        </div>
      </div>
    </div>

    <!-- Right Sign In Panel -->
    <section class="login-form-panel" aria-labelledby="login-title">
      <div class="panel-inner">
        <div class="form-header">
          <div class="form-brand" role="img" aria-label="Saya Intelligence">
            <span class="text-3xl font-black tracking-tighter text-base-content"
              >saya</span
            >
            <div class="flex items-baseline mb-0.5">
              <span class="text-xl font-black text-[#3b5998]">i</span>
              <span
                class="text-[9px] font-black uppercase tracking-[0.2em] text-base-content/50 ml-0.5"
                >ntelligence</span
              >
            </div>
          </div>

          <div class="header-titles">
            <h1
              id="login-title"
              class="text-2xl font-black tracking-tight text-base-content"
            >
              sign in
            </h1>
            <p class="text-xs text-base-content/60 mt-1 font-medium">
              Access your telemetry dashboard, devices, and alerts
            </p>
          </div>
        </div>

        {#if sessionExpired}
          <div role="status" class="login-notice">
            <Info size={18} aria-hidden="true" class="shrink-0 text-primary" />
            <p>
              <strong>Your session expired.</strong> Sign in again to continue.
            </p>
          </div>
        {/if}

        <form
          onsubmit={handleSubmit}
          novalidate
          aria-busy={loading}
          aria-describedby={error ? "login-error" : undefined}
          class={error ? "form-shake" : ""}
        >
          {#if error}
            <div id="login-error" role="alert" class="login-error">
              <CircleAlert
                size={18}
                aria-hidden="true"
                class="shrink-0 text-error"
              />
              <p class="text-xs font-semibold text-error leading-relaxed">
                {error}
              </p>
            </div>
          {/if}

          <div class="field-group">
            <label for="username" class="field-label">Username</label>
            <div class="input-wrapper">
              <input
                id="username"
                name="username"
                type="text"
                autocomplete="username"
                autocapitalize="none"
                spellcheck={false}
                required
                bind:value={username}
                bind:this={usernameInput}
                oninput={clearError}
                placeholder="Enter your username"
                aria-invalid={invalidField === "username" ? "true" : undefined}
                aria-describedby={invalidField === "username"
                  ? "login-error"
                  : undefined}
                disabled={loading}
                class="form-input"
              />
            </div>
          </div>

          <div class="field-group">
            <div class="flex items-center justify-between mb-1.5">
              <label for="password" class="field-label">Password</label>
            </div>
            <div class="input-wrapper relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autocomplete="current-password"
                autocapitalize="none"
                spellcheck={false}
                required
                bind:value={password}
                bind:this={passwordInput}
                placeholder="Enter your password"
                disabled={loading}
                oninput={clearError}
                aria-invalid={invalidField === "password" ? "true" : undefined}
                aria-describedby={invalidField === "password"
                  ? "login-error"
                  : undefined}
                class="form-input pr-11"
              />
              <button
                class="absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-lg text-base-content/40 hover:text-base-content/80 hover:bg-base-content/5 transition-all"
                type="button"
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-controls="password"
                disabled={loading}
                onclick={() => (showPassword = !showPassword)}
              >
                {#if showPassword}
                  <EyeOff size={16} aria-hidden="true" />
                {:else}
                  <Eye size={16} aria-hidden="true" />
                {/if}
              </button>
            </div>
          </div>

          <button
            type="submit"
            class="submit-btn relative group"
            disabled={loading}
          >
            {#if loading}
              <Loader2
                size={18}
                class="animate-spin text-white"
                aria-hidden="true"
              />
              <span role="status">Signing in…</span>
            {:else}
              Sign in
            {/if}
          </button>
        </form>

        <a
          class="login-recovery block text-center text-xs font-semibold text-[#3b5998] hover:text-[#324d85] hover:underline mt-4 py-2"
          href="#/password/recovery">Forgot your password?</a
        >

        <div class="form-footer">
          <p class="text-xs text-base-content/50 text-center">
            Need an account? Contact your administrator.
          </p>
        </div>
      </div>
    </section>
  </div>
</main>

<style>
  .login-page {
    min-height: 100svh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    background: var(--color-base-100);
    position: relative;
    overflow: hidden;
  }

  .login-container {
    width: 100%;
    max-width: 980px;
    min-height: 600px;
    display: grid;
    grid-template-columns: 1fr;
    background: var(--color-base-100);
    border: 1px solid var(--glass-border);
    border-radius: 1.75rem;
    overflow: hidden;
    box-shadow:
      0 25px 50px -12px rgba(0, 0, 0, 0.08),
      0 0 0 1px rgba(0, 0, 0, 0.03);
    transition: all 0.3s ease;
  }

  :global(html.dark) .login-container {
    box-shadow:
      0 25px 60px -15px rgba(0, 0, 0, 0.7),
      0 0 0 1px rgba(255, 255, 255, 0.06);
  }

  @media (min-width: 1024px) {
    .login-container {
      grid-template-columns: 1fr 1fr;
    }
  }

  /* Left Hero Styles */
  .login-hero {
    display: none;
    position: relative;
    padding: 3.5rem 3rem;
    overflow: hidden;
    color: white;
    background: linear-gradient(
      145deg,
      #091326 0%,
      #0f1d39 35%,
      #142750 70%,
      #0b162c 100%
    );
  }

  @media (min-width: 1024px) {
    .login-hero {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
  }

  .hero-bg-mesh {
    position: absolute;
    inset: 0;
    opacity: 0.65;
    background: radial-gradient(
        circle at 15% 20%,
        rgba(59, 89, 152, 0.4) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 85% 80%,
        rgba(37, 99, 235, 0.25) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 50% 45%,
        rgba(56, 189, 248, 0.16) 0%,
        transparent 55%
      );
    filter: blur(40px);
    animation: heroMeshMove 14s ease-in-out infinite alternate;
  }

  @keyframes heroMeshMove {
    0% {
      transform: scale(1) translate(0, 0);
    }
    100% {
      transform: scale(1.12) translate(-20px, 15px);
    }
  }

  .hero-content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
  }

  .hero-header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
  }

  .hero-brand {
    display: flex;
    align-items: center;
    gap: 0.625rem;
  }

  .hero-logo-img {
    height: 38px;
    width: auto;
    object-fit: contain;
    filter: brightness(0) invert(1);
  }

  .hero-lead {
    margin: 2.5rem 0 2rem 0;
  }

  .hero-title {
    font-size: 1.75rem;
    font-weight: 800;
    line-height: 1.25;
    letter-spacing: -0.03em;
    color: #ffffff;
  }

  .hero-desc {
    font-size: 0.875rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.7);
    margin-top: 0.75rem;
  }

  .hero-points {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    margin-bottom: 2rem;
  }

  .point-item {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
  }

  .point-icon-box {
    width: 34px;
    height: 34px;
    border-radius: 0.625rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.12);
    flex-shrink: 0;
  }

  .point-title {
    font-size: 0.8125rem;
    font-weight: 700;
    color: #ffffff;
  }

  .point-detail {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.55);
    margin-top: 0.15rem;
  }

  .hero-footer-note {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    padding-top: 1.25rem;
  }

  /* Right Form Panel Styles */
  .login-form-panel {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3rem 2.5rem;
    background: var(--color-base-100);
  }

  @media (max-width: 640px) {
    .login-form-panel {
      padding: 2.25rem 1.5rem;
    }
  }

  .panel-inner {
    width: 100%;
    max-width: 380px;
  }

  .form-header {
    margin-bottom: 2rem;
  }

  .form-brand {
    display: flex;
    align-items: baseline;
    gap: 0.125rem;
    margin-bottom: 1.25rem;
  }

  .field-group {
    margin-bottom: 1.25rem;
  }

  .field-label {
    display: block;
    font-size: 0.8125rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: var(--color-base-content);
  }

  .form-input {
    width: 100%;
    height: 48px;
    padding: 0 1rem;
    border-radius: 0.75rem;
    border: 1px solid var(--glass-border);
    background: var(--color-base-200);
    color: var(--color-base-content);
    font-size: 0.875rem;
    font-weight: 500;
    transition: all 0.2s ease;
    outline: none;
  }

  .form-input::placeholder {
    color: var(--color-base-content);
    opacity: 0.35;
  }

  .form-input:focus {
    border-color: #3b5998;
    background: var(--color-base-100);
    box-shadow: 0 0 0 3px rgba(59, 89, 152, 0.2);
  }

  .form-input[aria-invalid="true"] {
    border-color: var(--color-alert-status, #ef4444);
    box-shadow: 0 0 0 3px
      color-mix(in srgb, var(--color-alert-status, #ef4444) 15%, transparent);
  }

  .form-input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .submit-btn {
    width: 100%;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 1.75rem;
    border-radius: 0.75rem;
    background: #3b5998;
    color: #ffffff;
    border: none;
    cursor: pointer;
    font-weight: 700;
    letter-spacing: 0.01em;
    transition: all 0.2s ease;
    box-shadow: 0 4px 14px 0 rgba(59, 89, 152, 0.35);
  }

  .submit-btn:hover:not(:disabled) {
    background: #324d85;
    transform: translateY(-1px);
    box-shadow: 0 6px 20px 0 rgba(59, 89, 152, 0.45);
  }

  .submit-btn:active:not(:disabled) {
    background: #2a406f;
    transform: translateY(0);
  }

  .submit-btn:disabled {
    opacity: 0.7;
    cursor: wait;
  }

  .login-notice {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.875rem 1rem;
    border-radius: 0.75rem;
    background: color-mix(
      in srgb,
      var(--color-primary) 8%,
      var(--color-base-100)
    );
    border: 1px solid color-mix(in srgb, var(--color-primary) 25%, transparent);
    margin-bottom: 1.5rem;
  }

  .login-error {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.875rem 1rem;
    border-radius: 0.75rem;
    background: color-mix(
      in srgb,
      var(--color-alert-status, #ef4444) 8%,
      var(--color-base-100)
    );
    border: 1px solid
      color-mix(in srgb, var(--color-alert-status, #ef4444) 25%, transparent);
    margin-bottom: 1.25rem;
  }

  .form-footer {
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid var(--glass-border);
  }

  .form-shake {
    animation: shake 0.4s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
  }

  @keyframes shake {
    10%,
    90% {
      transform: translate3d(-1px, 0, 0);
    }
    20%,
    80% {
      transform: translate3d(2px, 0, 0);
    }
    30%,
    50%,
    70% {
      transform: translate3d(-3px, 0, 0);
    }
    40%,
    60% {
      transform: translate3d(3px, 0, 0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .hero-bg-mesh {
      animation: none;
    }
    .form-shake {
      animation: none;
    }
  }
</style>
